import { companyApi, jobApi, matchesApi, messagingApi } from '../../shared/utils/apiClient';

// ────────────────────────────────────────────────────────────────────────────
// Draft types — kept compatible with the workspace renderer
// ────────────────────────────────────────────────────────────────────────────

export interface StructuredQuery {
    query_text?: string;
    required_skills?: string[];
    preferred_skills?: string[];
    keywords?: string[];
    min_years_experience?: number;
    preferred_titles?: string[];
    preferred_companies?: string[];
    required_certifications?: string[];
    required_locations?: string[];
}

export interface DraftCandidateMatchedSignals {
    required_skills_matched?: string[];
    preferred_skills_matched?: string[];
    required_certifications_matched?: string[];
    preferred_titles_matched?: string[];
    preferred_companies_matched?: string[];
    keywords_matched?: string[];
    locations_matched?: string[];
}

export interface DraftCandidate {
    id: string;
    name: string;
    email?: string;
    headline?: string;
    titles?: string[];
    companies?: string[];
    skills?: string[];
    locations?: string[];
    years_experience?: number;
    source?: 'platform' | 'imported' | 'imported_se' | string;
    match_score?: number;
    vector_similarity?: number;
    qwen_score?: number;
    soft_criteria_score?: number;
    matched_signals?: DraftCandidateMatchedSignals;
    justification?: string;
}

export interface DraftResponse {
    recruiter_text: string;
    structured_query: StructuredQuery;
    total_retrieved: number;
    candidates: DraftCandidate[];
    draft: string;
}

// ────────────────────────────────────────────────────────────────────────────
// Job targeting — the primary composer flow
// ────────────────────────────────────────────────────────────────────────────

export interface JobSummary {
    id: string;
    title: string;
    companyId: string;
    companyName: string;
    location?: string;
    employmentType?: string;
    experienceLevel?: string;
}

/**
 * List all active jobs for the admin David inbox composer. The admin picks
 * a job and David finds candidates that match it — the target company is
 * derived from job.companyId so there's no separate company picker.
 */
export async function fetchJobsForTargeting(search?: string): Promise<JobSummary[]> {
    const qs = search && search.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
    const raw = await jobApi.get<any>(`/admin/v1/jobs${qs}`);
    // Job-Service wraps responses as {status, payload: {jobs: [...], total}}.
    // unwrapResponse only unwraps when payload has a `data` key, so we
    // manually peel the payload here before reading `jobs`.
    const unwrapped = raw?.payload ?? raw;
    const items: any[] = Array.isArray(unwrapped)
        ? unwrapped
        : unwrapped?.jobs ?? unwrapped?.items ?? [];
    return items.map((j) => ({
        id: j.id || j._id,
        title: j.title || '(untitled)',
        companyId: j.companyId || j.company_id || '',
        companyName: j.companyName || j.company_name || '',
        location: j.location,
        employmentType: j.employmentType || j.employment_type,
        experienceLevel: j.experienceLevel || j.experience_level,
    }));
}

export interface CompanyOwner {
    userId: string;
    name?: string;
    email?: string;
}

export interface CompanyDetail {
    id: string;
    name: string;
    industry?: string;
    logoUrl?: string;
    owner: CompanyOwner;
}

/**
 * Fetch a single company by ID to resolve its owner.user_id for the
 * send-as-david-to-company target. Called on-demand after the admin picks
 * a job — no prefetch of a full companies list.
 */
export async function fetchCompanyById(companyId: string): Promise<CompanyDetail | null> {
    if (!companyId) return null;
    try {
        const raw = await companyApi.get<any>(`/companies/v1/${companyId}`);
        // company-service wraps as {status, payload: {...company fields...}}.
        // unwrapResponse only unwraps {status, payload: {data: ...}} so we
        // peel the payload ourselves.
        const c: any = raw?.payload ?? raw ?? {};
        return {
            id: c.id || c._id || companyId,
            name: c.name || '(unnamed)',
            industry: c.industry,
            logoUrl: c.logoUrl || c.logo_url,
            owner: {
                userId: c.owner?.userId || c.owner?.user_id || '',
                name: c.owner?.name,
                email: c.owner?.email,
            },
        };
    } catch (err) {
        console.error('[davidInboxApi] fetchCompanyById failed', err);
        return null;
    }
}

// ────────────────────────────────────────────────────────────────────────────
// Job → candidate matches (hybrid retrieval + reranking)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Raw response shape returned by matches-vectors
 * `POST /admin/v1/jobs/{jobId}/find-candidates`.
 * Fields mirror matches-vectors/pkg/models/models.go CandidateMatch.
 */
interface RawCandidateMatch {
    candidate?: {
        id?: string;
        name?: string;
        email?: string;
        yearsExp?: number;
        allJobTitles?: string[];
        allCompanies?: string[];
        skills?: string[];
        locations?: string[];
        degreesList?: string[];
    };
    matchScore?: number;
    vectorSimilarity?: number;
    qwenScore?: number;
    softCriteriaScore?: number;
    source?: string;
    breakdown?: {
        softCriteriaMatches?: {
            titleMatches?: string[];
            companyMatches?: string[];
            schoolMatches?: string[];
            keywordMatches?: string[];
            skillMatches?: string[];
        };
    };
}

interface RawFindCandidatesResponse {
    jobId: string;
    matches?: RawCandidateMatch[];
    totalCandidates?: number;
    processingTimeMs?: number;
}

/**
 * Map a raw CandidateMatch into the DraftCandidate shape the workspace
 * renderer expects. Normalizes field names and folds softCriteriaMatches
 * into the flat matched_signals structure.
 */
function mapCandidateMatch(m: RawCandidateMatch): DraftCandidate {
    const c = m.candidate || {};
    const soft = m.breakdown?.softCriteriaMatches || {};
    return {
        id: c.id || '',
        name: c.name || '(unknown)',
        email: c.email,
        titles: c.allJobTitles,
        companies: c.allCompanies,
        skills: c.skills,
        locations: c.locations,
        years_experience: c.yearsExp,
        source: m.source,
        match_score: m.matchScore,
        vector_similarity: m.vectorSimilarity,
        qwen_score: m.qwenScore,
        soft_criteria_score: m.softCriteriaScore,
        matched_signals: {
            required_skills_matched: soft.skillMatches,
            preferred_titles_matched: soft.titleMatches,
            preferred_companies_matched: soft.companyMatches,
            keywords_matched: soft.keywordMatches,
        },
    };
}

/**
 * Run the job-based candidate search + draft pipeline.
 *
 * 1. Calls matches-vectors /admin/v1/jobs/{jobId}/find-candidates (hybrid
 *    retrieval + rerank across all 3 collections, same endpoint we verified
 *    end-to-end in §11 on prod).
 * 2. Folds the response into the DraftResponse shape the workspace already
 *    knows how to render.
 * 3. Synthesizes an editable draft-text template from the job + company +
 *    top candidates. No LLM round-trip — the template is deterministic so
 *    the admin can edit it freely and send.
 */
export async function runJobBasedDraft(
    job: JobSummary,
    company: CompanyDetail | null,
    topN = 5
): Promise<DraftResponse> {
    const raw = await matchesApi.post<RawFindCandidatesResponse>(
        `/admin/v1/jobs/${job.id}/find-candidates`,
        { limit: 20 }
    );

    const allMatches = (raw?.matches || []).map(mapCandidateMatch);
    const topMatches = allMatches.slice(0, topN);

    const structured: StructuredQuery = {
        query_text: `Candidates for ${job.title} at ${job.companyName}`,
    };

    const recruiter_text = buildRecruiterTextForJob(job);
    const draft = buildDraftTextForJob(job, company, topMatches);

    return {
        recruiter_text,
        structured_query: structured,
        total_retrieved: raw?.totalCandidates ?? allMatches.length,
        candidates: topMatches,
        draft,
    };
}

/** Human-readable recruiter-prompt synthesized from the job context. Used
 *  only for display in the interpretation card and for history preservation. */
function buildRecruiterTextForJob(job: JobSummary): string {
    const pieces: string[] = [`Find the best candidates for ${job.title}`];
    if (job.companyName) pieces.push(`at ${job.companyName}`);
    if (job.location) pieces.push(`in ${job.location}`);
    if (job.experienceLevel) pieces.push(`(${job.experienceLevel})`);
    return pieces.join(' ') + '.';
}

/**
 * Template-driven draft text builder. Deterministic, no LLM. The admin
 * edits it freely before sending.
 */
function buildDraftTextForJob(
    job: JobSummary,
    company: CompanyDetail | null,
    candidates: DraftCandidate[]
): string {
    const salutation = company?.owner?.name
        ? `Hi ${company.owner.name.split(' ')[0]},`
        : 'Hi there,';

    const intro = `we've lined up ${candidates.length} strong candidates for your ${job.title} role${job.companyName ? ` at ${job.companyName}` : ''}. Here are the highlights:`;

    const lines = candidates.map((c, i) => {
        const primaryTitle = c.titles && c.titles[0] ? ` — ${c.titles[0]}` : '';
        const years = typeof c.years_experience === 'number' && c.years_experience > 0
            ? ` · ${c.years_experience}y exp`
            : '';
        const qwen = typeof c.qwen_score === 'number'
            ? `  Match quality: ${c.qwen_score.toFixed(0)}/100`
            : '';
        const skills = c.skills && c.skills.length > 0
            ? `  Strong in: ${c.skills.slice(0, 5).join(', ')}`
            : '';
        return `${i + 1}. ${c.name}${primaryTitle}${years}\n${qwen}${skills ? '\n' + skills : ''}`;
    });

    const closing = `\nLet me know which profiles you'd like to move forward with and I'll set up introductions.\n\nBest,\nDavid`;

    return `${salutation}\n\n${intro}\n\n${lines.join('\n\n')}\n${closing}`;
}

// ────────────────────────────────────────────────────────────────────────────
// Send to company owner (messaging-service-go david_company_* collections)
// ────────────────────────────────────────────────────────────────────────────

export interface CandidateRecommendation {
    candidate_id: string;
    name: string;
    headline?: string;
    source?: string;
    years_experience?: number;
    titles?: string[];
    companies?: string[];
    skills?: string[];
    locations?: string[];
    qwen_score?: number;
    vector_similarity?: number;
    match_score?: number;
    matched_skills?: string[];
    matched_certs?: string[];
    matched_titles?: string[];
    matched_keywords?: string[];
    justification?: string;
}

export function draftCandidateToRecommendation(c: DraftCandidate): CandidateRecommendation {
    return {
        candidate_id: c.id,
        name: c.name,
        headline: c.headline,
        source: c.source,
        years_experience: c.years_experience,
        titles: c.titles,
        companies: c.companies,
        skills: c.skills,
        locations: c.locations,
        qwen_score: c.qwen_score,
        vector_similarity: c.vector_similarity,
        match_score: c.match_score,
        matched_skills: c.matched_signals?.required_skills_matched?.concat(
            c.matched_signals?.preferred_skills_matched ?? []
        ),
        matched_certs: c.matched_signals?.required_certifications_matched,
        matched_titles: c.matched_signals?.preferred_titles_matched,
        matched_keywords: c.matched_signals?.keywords_matched,
        justification: c.justification,
    };
}

export interface SendAsDavidToCompanyRequest {
    target_user_id: string;
    target_role?: 'company' | 'recruiter';
    company_id?: string;
    company_name?: string;
    content: string;
    candidate_recommendations?: CandidateRecommendation[];
    recruiter_query?: string;
    structured_query?: StructuredQuery;
    original_draft?: string;
}

export interface SendAsDavidToCompanyResponse {
    conversation_id: string;
    message_id: string;
    created_at: string;
}

export async function sendAsDavidToCompany(
    req: SendAsDavidToCompanyRequest
): Promise<SendAsDavidToCompanyResponse> {
    return messagingApi.post<SendAsDavidToCompanyResponse>(
        '/admin/send-as-david-to-company',
        req
    );
}

// ────────────────────────────────────────────────────────────────────────────
// Conversation history
// ────────────────────────────────────────────────────────────────────────────

export interface DavidCompanyConversation {
    id: string;
    target_user_id: string;
    target_role: string;
    target_name?: string;
    company_id?: string;
    company_name?: string;
    last_message?: {
        content: string;
        sender_id: string;
        timestamp: string;
    };
    created_at: string;
    updated_at: string;
}

export interface DavidCompanyMessage {
    id: string;
    conversation_id: string;
    sender_type: 'david_bot' | 'company' | string;
    sender_name: string;
    content: string;
    message_type: 'text' | 'candidate_recommendation' | string;
    candidate_recommendations?: CandidateRecommendation[];
    recruiter_query?: string;
    structured_query?: StructuredQuery;
    original_draft?: string;
    created_at: string;
}

export async function listDavidCompanyConversations(): Promise<DavidCompanyConversation[]> {
    const { conversations } = await messagingApi.get<{
        conversations: DavidCompanyConversation[];
    }>('/admin/david-company-conversations');
    return conversations ?? [];
}

export async function getDavidCompanyMessages(conversationId: string): Promise<DavidCompanyMessage[]> {
    const { messages } = await messagingApi.get<{
        messages: DavidCompanyMessage[];
    }>(`/admin/david-company-messages?conversation_id=${encodeURIComponent(conversationId)}`);
    return messages ?? [];
}
